import { FC, MouseEvent } from 'react';
import { Checkbox } from '@mui/material';
import clsx from 'clsx';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { TreeItem as TreeItemType } from './types';

interface TreeItemProps<T = any> {
  item: TreeItemType<T>;
  level: 'main' | 'sub' | 'subSub';
  onToggle: () => void;
  expanded: boolean;
  onExpandToggle?: () => void;
  renderItem?: (item: TreeItemType<T>, level: 'main' | 'sub' | 'subSub') => React.ReactNode;
  isMultiSelect: boolean;
}

export const TreeItem: FC<TreeItemProps> = ({ item, level, onToggle, expanded, onExpandToggle, renderItem, isMultiSelect }) => {
  // Check if item has sub-items
  const hasSubItems = item.subItems && item.subItems.length > 0;

  // Handle expand/collapse toggle
  const handleExpandToggle = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onExpandToggle) {
      onExpandToggle();
    }
  };

  // Use custom render function if provided
  if (renderItem) {
    return <>{renderItem(item, level)}</>;
  }

  // Determine text size based on level
  const textSize = 'text-14 xl:text-16';

  // Determine status color based on metadata or default
  const statusColor = item.metadata?.statusColor;

  // Handler for main row click - handles both selection and expansion
  const handleRowClick = (e: MouseEvent) => {
    e.preventDefault();
    onToggle();
  };

  return (
    <button
      className={clsx('flex items-center gap-2 p-6 rounded-4 hover:bg-neutral-10 cursor-pointer w-full', !isMultiSelect && item.selected && 'bg-neutral-10')}
      onClick={handleRowClick}
      tabIndex={0}
      aria-label={`Toggle ${item.label}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
    >
      {/* Checkbox for multi-select */}
      {isMultiSelect && (
        <Checkbox
          checked={item.selected}
          onChange={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          onClick={(e) => e.stopPropagation()}
          sx={{
            color: '#DFDFDF',
            '&.Mui-checked': {
              color: '#00A651',
            },
            padding: 0,
          }}
          inputProps={{
            'aria-label': `Toggle checkbox for ${item.label}`,
          }}
        />
      )}

      {/* Expand/collapse button */}
      {hasSubItems && level !== 'subSub' && (
        <button
          onClick={handleExpandToggle}
          tabIndex={0}
          className='flex items-center justify-center cursor-pointer hover:bg-neutral-20 rounded-full w-24 h-24 mx-1'
          aria-label='Toggle sub-entities'
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleExpandToggle(e as any);
            }
          }}
        >
          {expanded ? (
            <KeyboardArrowDownIcon className='text-neutral-40 !text-[28px] xl:!text-30' />
          ) : (
            <KeyboardArrowRightIcon className='text-neutral-40 !text-[28px] xl:!text-30' />
          )}
        </button>
      )}
      {!hasSubItems && <div className='w-8' />}

      {/* Label */}
      <span className={textSize}>{item.label}</span>

      {/* Status indicators */}
      <div className='flex items-center gap-8 ml-auto'>
        {item.metadata?.statusCount && item.metadata.statusCount > 0 && (
          <div className='py-4 px-6 bg-neutral-10 rounded-8 text-12 font-bold text-center' style={{ boxShadow: '0 2px 4px 0 rgba(0,0,0,0.05)' }}>
            {item.metadata.statusCount}
          </div>
        )}
        {statusColor && <div className={clsx(`w-8 h-8 rounded-full`, statusColor)} />}
      </div>
    </button>
  );
};
